<template>
  <span class="rating-display" :class="'rating-display--' + size" :style="isLabels ? null : { color: effectiveColor }">
    <span v-if="isLabels && levelName" class="tag-chip text-white" :style="tagChipStyle(effectiveColor)">{{ levelName }}</span>
    <span v-else class="rating-display__icons" role="img" :aria-label="ariaLabel">
      <span v-for="n in max" :key="n" class="rating-display__icon" :class="'is-' + fill(n)">
        <i v-if="custom" class="rating-display__outline tim-icons" :class="effectiveIcon"></i>
        <b-icon v-else class="rating-display__outline" :icon="effectiveIcon"></b-icon>
        <span v-if="fill(n) !== 'empty'" class="rating-display__fill">
          <i v-if="custom" class="tim-icons" :class="filledIconName"></i>
          <b-icon v-else :icon="filledIconName"></b-icon>
        </span>
      </span>
    </span>
  </span>
</template>

<script lang="ts">
export default { name: 'rating-display' }
</script>

<script setup lang="ts">
import { computed } from 'vue'
import { filledIcon, formatRatingValue, iconFill, isCustomRatingIcon, parseLevels, ratingMax } from '@/util/ratings'
import { tagChipStyle } from '@/util/tagChip'

// Read-only rating: icons with half fill, or a named level. `scale` is a rating scale or a shaped
// rating from the API (both carry display_type, max_value, levels, icon, color); `icon` and
// `color` override the scale's for one event.
const props = defineProps({
  value: { type: Number, default: null },
  scale: { type: Object, required: true },
  icon: { type: String, default: null },
  color: { type: String, default: null },
  size: { type: String, default: 'md' }, // xs | sm | md | lg
})

const isLabels = computed(() => props.scale.display_type === 'labels')
const max = computed(() => ratingMax(props.scale as any))
const effectiveIcon = computed(() => props.icon || props.scale.icon || 'star')
const effectiveColor = computed(() => props.color || props.scale.color || '#e14eca')
const custom = computed(() => isCustomRatingIcon(effectiveIcon.value))
const filledIconName = computed(() => filledIcon(effectiveIcon.value))
const levelName = computed(() => parseLevels(props.scale.levels)[Number(props.value) - 1] || '')
const ariaLabel = computed(() => props.value == null ? 'Not rated' : formatRatingValue(props.value, props.scale as any))

function fill(n: number) {
  return iconFill(props.value, n)
}
</script>

<style lang="scss">
.rating-display {
  display: inline-flex;
  align-items: center;
  line-height: 1;

  &--xs { font-size: 0.75rem; }
  &--sm { font-size: 0.875rem; }
  &--md { font-size: 1.05rem; }
  &--lg { font-size: 1.3rem; }

  // Spacing scales with the icon size so wide glyphs (envelope, book, alarm) never touch.
  &__icons {
    display: inline-flex;
    gap: 0.25em;
  }

  &__icon {
    position: relative;
    display: inline-flex;

    &.is-empty .rating-display__outline,
    &.is-half .rating-display__outline {
      opacity: 0.4;
    }

    // Filled glyphs carry their details as cut-outs (keyhole, skull eyes). The outline must not sit
    // under the filled part or its strokes fill those holes: hide it on full icons and keep only its
    // right half on half icons.
    &.is-full .rating-display__outline {
      visibility: hidden;
    }

    &.is-half .rating-display__outline {
      clip-path: inset(0 0 0 50%);
    }
  }

  &__fill {
    position: absolute;
    top: 0;
    left: 0;
    display: inline-flex;
  }

  &__icon.is-half &__fill {
    clip-path: inset(0 50% 0 0);
  }

}

// Empty and half outlines need more weight on a white background to stay legible.
.white-content .rating-display__icon.is-empty .rating-display__outline,
.white-content .rating-display__icon.is-half .rating-display__outline {
  opacity: 0.6;
}
</style>
